import { BehaviorSubject, Observable } from "rxjs";

interface ICreateStateHelper<T> {
  /** Subscribe to the state value */
  $: Observable<T>,
  /** Assign a new value to the state */
  set: (value: T) => void;
  /** Map the state value over an updating function */
  update: (fn: (curernt: T) => T) => void;
  /** Reset the state value to its initial state */
  reset: () => void;
  /** Cleanup the state subject */
  complete: () => void;
  /** Convenience property to retrieve the behaviour subject value */
  readonly value: T;
}

/**
 * Helper to create a component state variable backed by a behaviour subject
 */
export function createState<T>(initial: T): ICreateStateHelper<T> {
  const subject = new BehaviorSubject<T>(initial);
  return {
    $: subject.asObservable(),
    set: (value: T) => subject.next(value),
    update: (fn: (curernt: T) => T) => subject.next(fn(subject.value)),
    reset: () => subject.next(initial),
    complete: () => subject.complete(),
    get value() { return subject.value },
  }
}

/**
 * Helper to create nullable component state variable backed by a behaviour subject
 */
export function createNullableState<T>(initial: T | null = null) {
  return createState<T | null>(initial);
}
